import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, Save, Users, RefreshCw, Database, FileSpreadsheet, CheckCircle, Trash2, Lock, Send, Clock, FileDown, Upload } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useAlert } from '../../context/AlertContext';
import TabNavigation from '../../components/TabNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Blank optional inputs must reach the API as null, not NaN — parseInt('')
// is NaN, which serializes to null-ish garbage and fails validation.
const optionalInt = (v) => (
  v === '' || v === null || v === undefined ? null : parseInt(v, 10)
);
const optionalFloat = (v) => (
  v === '' || v === null || v === undefined ? null : parseFloat(v)
);

/** Twelve blank month rows. Shared so the grid's shape is defined once. */
const emptyMonthRows = (year) => MONTHS.map((_, idx) => ({
  period_month: idx + 1,
  period_year: year,
  total_employees: '',
  total_contractors: '',
  total_consultants: '',
  total_manhours: '',
  employee_manhours: '',
  contractor_manhours: '',
  consultant_manhours: ''
}));

const WorkforceInput = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showSuccess, showError, confirm } = useAlert();
  const {
    activeSiteId, activeSiteName, isCorpUser, availableSites,
    registerSiteSwitchGuard,
  } = useSite();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);
  // True once the grid has been edited but not yet saved. Drives the confirm
  // shown when the header's site selector would discard those edits.
  const [dirty, setDirty] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [workforceData, setWorkforceData] = useState([]);
  const [hrmsData, setHrmsData] = useState({});
  const [hasHrmsData, setHasHrmsData] = useState(false);
  const [dataSource, setDataSource] = useState('manual'); // 'manual' or 'combined'

  // --- Edit-access workflow state ---
  const [editAccess, setEditAccess] = useState(null); // { can_edit, is_admin, request }
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestJustification, setRequestJustification] = useState('');

  // Initialize 12 months of empty data
  useEffect(() => {
    setWorkforceData(emptyMonthRows(selectedYear));
    setHrmsData({});
    setHasHrmsData(false);
    setDirty(false);
  }, [selectedYear]);

  // Switching site must not carry the previous site's typed values over. The
  // grid is reset here and repopulated by fetchYearlyData, so a failed reload
  // leaves it blank rather than showing another site's numbers.
  // Intentionally keyed on activeSiteId alone: this is the site-change reset.
  // The year-change reset is the effect above.
  useEffect(() => {
    setWorkforceData(emptyMonthRows(selectedYear));
    setDirty(false);
  }, [activeSiteId]);  // eslint-disable-line

  // Confirm before the header's site selector discards unsaved grid edits. The
  // guard runs BEFORE the switch, so cancelling keeps both the site and the
  // typed values; the reset above is the safety net if the switch goes ahead.
  useEffect(() => {
    if (!dirty) return undefined;
    return registerSiteSwitchGuard(async () => confirm(
      'Discard unsaved workforce data?',
      'This year has unsaved changes for the current site. '
      + 'Switching site will discard them.',
      { confirmLabel: 'Discard', destructive: true },
    ));
  }, [dirty, registerSiteSwitchGuard, confirm]);

  const isAllSitesMode = !activeSiteId && availableSites.length > 1;

  // canEdit:
  //   - false in All-Sites mode (always read-only across sites)
  //   - true if backend says is_admin or can_edit
  //   - false if backend hasn't responded yet (fail closed)
  const canEdit = !isAllSitesMode && !!(editAccess?.is_admin || editAccess?.can_edit);
  const requestStatus = editAccess?.request?.status || null; // 'pending'|'approved'|'denied'|'revoked'|null

  // Sync selected site from SiteContext (works for all users)
  useEffect(() => {
    if (activeSiteId) {
      setSelectedSiteId(activeSiteId);
    } else if (isAllSitesMode) {
      // "All Sites" selected — use special marker
      setSelectedSiteId('__all__');
    }
  }, [activeSiteId, isAllSitesMode]);

  // Fetch existing data when site/year changes
  useEffect(() => {
    if (selectedSiteId && selectedYear) {
      fetchYearlyData();
    }
  }, [selectedSiteId, selectedYear]);

  // Fetch the user's edit-access status whenever the selected site changes.
  // "All Sites" is always read-only — we don't query and we don't need a grant.
  useEffect(() => {
    if (selectedSiteId && selectedSiteId !== '__all__') {
      fetchEditAccess();
    } else {
      setEditAccess(null);
    }
  }, [selectedSiteId]);

  const fetchEditAccess = async () => {
    try {
      const res = await axios.get(
        `${API}/workforce/access-requests/my-status?site_id=${selectedSiteId}`
      );
      setEditAccess(res.data);
      setShowRequestForm(false);
      setRequestJustification('');
    } catch (err) {
      console.error('Failed to fetch edit access:', err);
      // Fail closed — treat as no access
      setEditAccess({ can_edit: false, is_admin: false, request: null });
    }
  };

  const handleRequestAccess = async () => {
    if (!selectedSiteId || selectedSiteId === '__all__') return;
    setRequestingAccess(true);
    try {
      await axios.post(`${API}/workforce/access-requests`, {
        site_id: selectedSiteId,
        justification: requestJustification || null,
      });
      showSuccess('Access request submitted. An administrator will review it.');
      setShowRequestForm(false);
      setRequestJustification('');
      await fetchEditAccess();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to submit access request');
    } finally {
      setRequestingAccess(false);
    }
  };

  const fetchYearlyData = async () => {
    try {
      setLoading(true);
      // Use aggregated endpoint when "All Sites" is selected
      const url = selectedSiteId === '__all__'
        ? `${API}/workforce/all-sites/workforce/yearly?year=${selectedYear}`
        : `${API}/workforce/sites/${selectedSiteId}/workforce/yearly?year=${selectedYear}`;
      const response = await axios.get(url);

      const { months: monthsData, has_hrms_data } = response.data;
      setHasHrmsData(has_hrms_data);

      // Build HRMS data map
      const hrmsMap = {};
      monthsData.forEach(m => {
        if (m.hrms) {
          hrmsMap[m.period_month] = m.hrms;
        }
      });
      setHrmsData(hrmsMap);

      // Merge snapshot data into the 12-month template.
      // If a month has no snapshot (e.g. after delete), reset inputs to empty
      // so stale local state doesn't linger.
      setWorkforceData(prev => prev.map((month, idx) => {
        const monthEntry = monthsData[idx];
        const snapshot = monthEntry?.snapshot;
        if (snapshot) {
          return {
            ...month,
            id: snapshot.id,
            total_employees: snapshot.total_employees ?? '',
            total_contractors: snapshot.total_contractors ?? '',
            total_consultants: snapshot.total_consultants ?? '',
            total_manhours: snapshot.total_manhours ?? '',
            employee_manhours: snapshot.employee_manhours ?? '',
            contractor_manhours: snapshot.contractor_manhours ?? '',
            consultant_manhours: snapshot.consultant_manhours ?? ''
          };
        }
        // No snapshot — reset to empty (important after delete)
        return {
          ...month,
          id: undefined,
          total_employees: '',
          total_contractors: '',
          total_consultants: '',
          total_manhours: '',
          employee_manhours: '',
          contractor_manhours: '',
          consultant_manhours: ''
        };
      }));
    } catch (err) {
      // Fallback to old endpoint
      if (err.response?.status === 404 || err.response?.status === 405) {
        fetchWorkforceDataLegacy();
      } else {
        console.error('Failed to fetch workforce data:', err);
      }
    } finally {
      setLoading(false);
      // The grid now mirrors what is stored, so there is nothing unsaved.
      setDirty(false);
    }
  };

  // Legacy fetch for backward compatibility
  const fetchWorkforceDataLegacy = async () => {
    try {
      for (let m = 1; m <= 12; m++) {
        try {
          const response = await axios.get(
            `${API}/workforce/sites/${selectedSiteId}/workforce?year=${selectedYear}&month=${m}`
          );
          const existing = response.data?.data;
          if (existing) {
            setWorkforceData(prev => prev.map(row => {
              if (row.period_month === m) {
                return {
                  ...row,
                  id: existing.id,
                  total_employees: existing.total_employees ?? '',
                  total_contractors: existing.total_contractors ?? '',
                  total_consultants: existing.total_consultants ?? '',
                  total_manhours: existing.total_manhours ?? '',
                  employee_manhours: existing.employee_manhours ?? '',
                  contractor_manhours: existing.contractor_manhours ?? '',
                  consultant_manhours: existing.consultant_manhours ?? ''
                };
              }
              return row;
            }));
          }
        } catch {
          // No data for this month
        }
      }
    } catch (err) {
      console.error('Failed to fetch workforce data:', err);
    }
  };

  const handleCellChange = (monthIdx, field, value) => {
    setDirty(true);
    setWorkforceData(prev => prev.map((row, idx) => {
      if (idx !== monthIdx) return row;
      const updated = { ...row, [field]: value };
      // Auto-calculate total_manhours from all three exposure sources.
      // Consultant hours ARE included: consultants working on site contribute
      // exposure, so they belong in the TRIR / LTIFR / FR denominator.
      if (field === 'employee_manhours' || field === 'contractor_manhours'
          || field === 'consultant_manhours') {
        const pick = (name) => parseFloat(field === name ? value : row[name]) || 0;
        const total = pick('employee_manhours') + pick('contractor_manhours')
          + pick('consultant_manhours');
        updated.total_manhours = total > 0 ? total.toString() : '';
      }
      return updated;
    }));
  };

  const handleDeleteMonth = async (monthIdx) => {
    if (!selectedSiteId) return;
    const row = workforceData[monthIdx];
    const monthName = MONTHS[monthIdx];

    // If row has no saved id, just clear the inputs locally
    if (!row.id) {
      setDirty(true);
      setWorkforceData(prev => prev.map((r, i) =>
        i === monthIdx ? {
          ...r,
          total_employees: '', total_contractors: '', total_consultants: '',
          total_manhours: '', employee_manhours: '', contractor_manhours: '',
          consultant_manhours: ''
        } : r
      ));
      return;
    }

    const confirmed = await confirm(
      'Delete Workforce Data',
      `Are you sure you want to delete workforce data for ${monthName} ${selectedYear}? This action cannot be undone.`,
      { destructive: true, confirmLabel: 'Delete' }
    );
    if (!confirmed) return;

    try {
      await axios.delete(
        `${API}/workforce/sites/${selectedSiteId}/workforce?year=${selectedYear}&month=${row.period_month}`
      );
      showSuccess(`Deleted workforce data for ${monthName}`);
      fetchYearlyData();
    } catch (err) {
      console.error('Failed to delete workforce data:', err);
      showError(err.response?.data?.detail || 'Failed to delete workforce data');
    }
  };

  /** Download the year's CSV template, pre-filled with anything already saved. */
  const handleDownloadTemplate = async () => {
    if (!selectedSiteId || isAllSitesMode) {
      showError('Select a single site first.');
      return;
    }
    setDownloadingTemplate(true);
    try {
      const resp = await axios.get(
        `${API}/workforce/sites/${selectedSiteId}/workforce/template`,
        { params: { year: selectedYear }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workforce_template_${selectedYear}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download template:', err);
      showError(err.response?.data?.detail || 'Failed to download the template.');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  /** Upload a filled template. The server validates the whole file first, so a
   *  rejected file leaves the year untouched. */
  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    // Reset immediately so re-picking the same file fires onChange again.
    event.target.value = '';
    if (!file) return;

    if (!selectedSiteId || isAllSitesMode) {
      showError('Select a single site before uploading.');
      return;
    }

    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await axios.post(
        `${API}/workforce/sites/${selectedSiteId}/workforce/import`,
        form,
        { params: { year: selectedYear } }
      );
      showSuccess(resp.data?.message || 'Workforce data imported.');
      setDirty(false);
      fetchYearlyData();
    } catch (err) {
      console.error('Failed to import workforce CSV:', err);
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        // Surface the row-level problems rather than a generic failure.
        const lines = detail.errors
          .map(e => (e.row ? `Row ${e.row}: ${e.message}` : e.message))
          .join('\n');
        showError(`${detail.message}\n\n${lines}`);
      } else {
        showError(typeof detail === 'string' ? detail : 'Failed to import the CSV.');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleSave = async () => {
    if (!selectedSiteId) {
      showError('Please select a site first.');
      return;
    }

    setSaving(true);
    try {
      // Find rows that have partial data — these are invalid
      const requiredFields = ['total_employees', 'total_contractors', 'employee_manhours', 'contractor_manhours'];
      const partialRows = workforceData
        .map((row, idx) => ({ row, idx }))
        .filter(({ row }) => {
          const filledCount = requiredFields.filter(f => row[f] !== '' && row[f] !== null && row[f] !== undefined).length;
          return filledCount > 0 && filledCount < requiredFields.length;
        });

      if (partialRows.length > 0) {
        const monthNames = partialRows.map(({ idx }) => MONTHS[idx]).join(', ');
        showError(`Please fill all fields (Total Employees, Total Contractors, Employee Manhours, Contractor Manhours) for: ${monthNames}`);
        setSaving(false);
        return;
      }

      // Only save rows where ALL required fields are filled
      const dataToSave = workforceData
        .filter(row => requiredFields.every(f => row[f] !== '' && row[f] !== null && row[f] !== undefined))
        .map(row => ({
          period_year: selectedYear,
          period_month: row.period_month,
          total_employees: parseInt(row.total_employees),
          total_contractors: parseInt(row.total_contractors),
          total_manhours: parseFloat(row.total_manhours),
          employee_manhours: parseFloat(row.employee_manhours),
          contractor_manhours: parseFloat(row.contractor_manhours),
          // Consultants are optional — a site with none leaves these blank
          // rather than being made to enter 0, so send null, not NaN.
          total_consultants: optionalInt(row.total_consultants),
          consultant_manhours: optionalFloat(row.consultant_manhours)
        }));

      if (dataToSave.length === 0) {
        showError('Please fill all fields for at least one month.');
        setSaving(false);
        return;
      }

      // Validate: manhours must be positive
      const invalid = dataToSave.find(row =>
        (row.total_manhours !== null && row.total_manhours < 0) ||
        (row.employee_manhours !== null && row.employee_manhours < 0) ||
        (row.contractor_manhours !== null && row.contractor_manhours < 0)
      );
      if (invalid) {
        showError('Manhours must be positive numbers.');
        setSaving(false);
        return;
      }

      // Single bulk API call for all months
      if (dataToSave.length > 0) {
        await axios.post(
          `${API}/workforce/sites/${selectedSiteId}/workforce/bulk`,
          { entries: dataToSave }
        );
      }

      showSuccess('Workforce data saved successfully.');
      setDirty(false);
      fetchYearlyData();
    } catch (err) {
      console.error('Failed to save workforce data:', err);
      showError(err.response?.data?.detail || 'Failed to save workforce data.');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncFromHRMS = async () => {
    if (!selectedSiteId) {
      showError('Please select a site first.');
      return;
    }

    setSyncing(true);
    try {
      const response = await axios.post(
        `${API}/workforce/sites/${selectedSiteId}/workforce/sync-from-hrms?year=${selectedYear}`
      );
      showSuccess(`Synced ${response.data.months_synced} months from HRMS data.`);
      fetchYearlyData();
    } catch (err) {
      console.error('Failed to sync from HRMS:', err);
      showError(err.response?.data?.detail || 'Failed to sync from HRMS. Ensure HRMS data has been imported.');
    } finally {
      setSyncing(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  // Compute HRMS summary for the selected year
  const hrmsSummary = Object.values(hrmsData).length > 0 ? {
    totalHeadcount: Math.max(...Object.values(hrmsData).map(d => d.total_headcount || 0)),
    employees: Math.max(...Object.values(hrmsData).map(d => d.total_employees || 0)),
    contractors: Math.max(...Object.values(hrmsData).map(d => d.total_contractors || 0)),
    maleCount: Math.max(...Object.values(hrmsData).map(d => d.male_count || 0)),
    femaleCount: Math.max(...Object.values(hrmsData).map(d => d.female_count || 0)),
    totalManhours: Object.values(hrmsData).reduce((sum, d) => sum + (d.total_manhours || 0), 0),
    monthsWithData: Object.keys(hrmsData).length,
  } : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button variant="outline" onClick={() => navigate('/safety')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Safety Hub
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Workforce Data</h1>
              <p className="text-gray-600 mt-1">Monthly workforce headcount and manhours per site</p>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* HRMS Data Summary */}
        {selectedSiteId && hasHrmsData && hrmsSummary && (
          <Card className="mb-6 border-blue-200 bg-blue-50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base text-blue-800">
                <Database className="w-5 h-5" />
                HRMS Data Available
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-900">{hrmsSummary.totalHeadcount}</div>
                  <div className="text-xs text-blue-600">Total Headcount</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-900">{hrmsSummary.employees}</div>
                  <div className="text-xs text-blue-600">Permanent</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-900">{hrmsSummary.contractors}</div>
                  <div className="text-xs text-blue-600">Contractors</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-900">{hrmsSummary.maleCount}</div>
                  <div className="text-xs text-blue-600">Male</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-900">{hrmsSummary.femaleCount}</div>
                  <div className="text-xs text-blue-600">Female</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-900">{hrmsSummary.totalManhours.toFixed(0)}</div>
                  <div className="text-xs text-blue-600">Total Manhours</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-900">{hrmsSummary.monthsWithData}</div>
                  <div className="text-xs text-blue-600">Months w/ Data</div>
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleSyncFromHRMS}
                    disabled={syncing}
                    className="bg-[#1B5C94] hover:bg-[#144a78] text-white"
                  >
                    {syncing ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing...</>
                    ) : (
                      <><FileSpreadsheet className="w-4 h-4 mr-2" /> Sync from HRMS Data</>
                    )}
                  </Button>
                  <span className="text-xs text-blue-600">
                    Populates the table below with headcount and manhours computed from imported HRMS data
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Data Table */}
        {!selectedSiteId || selectedSiteId === '' ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Select a site from the header to view workforce data.</p>
            </CardContent>
          </Card>
        ) : loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                {selectedYear} Workforce Data
                {isAllSitesMode ? ' — All Sites (Aggregated)' : activeSiteName ? ` — ${activeSiteName}` : ''}
              </CardTitle>
              {isAllSitesMode && (
                <p className="text-sm text-blue-600 mt-1">
                  Showing aggregated data from all sites. Select a specific site to edit.
                </p>
              )}
            </CardHeader>

            {/* Edit-access banner — only when a specific site is selected */}
            {!isAllSitesMode && editAccess && !editAccess.is_admin && (
              <div className="px-6 -mt-2 mb-2">
                {editAccess.can_edit ? (
                  <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Edit access approved. You can save changes for {activeSiteName || 'this site'}.</span>
                  </div>
                ) : requestStatus === 'pending' ? (
                  <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    <Clock className="w-4 h-4" />
                    <span>Your edit-access request is pending approval by a site administrator.</span>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-3">
                    <div className="flex items-start gap-2 text-sm text-blue-800">
                      <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium">Read-only access.</p>
                        <p className="text-xs mt-0.5">
                          Workforce edit access is restricted. Request approval from a site administrator to add or modify data.
                          {requestStatus === 'denied' && editAccess?.request?.decision_note && (
                            <> Previous request denied: <em>{editAccess.request.decision_note}</em></>
                          )}
                          {requestStatus === 'revoked' && (
                            <> Your previous access was revoked
                              {editAccess?.request?.decision_note ? <>: <em>{editAccess.request.decision_note}</em></> : '.'}
                            </>
                          )}
                        </p>

                        {showRequestForm ? (
                          <div className="mt-2 space-y-2">
                            <textarea
                              value={requestJustification}
                              onChange={(e) => setRequestJustification(e.target.value)}
                              placeholder="Optional: explain why you need edit access…"
                              rows={2}
                              className="w-full text-sm px-2 py-1.5 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              maxLength={2000}
                            />
                            <div className="flex gap-2">
                              <Button
                                onClick={handleRequestAccess}
                                disabled={requestingAccess}
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                {requestingAccess ? (
                                  <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Sending…</>
                                ) : (
                                  <><Send className="w-3.5 h-3.5 mr-1" /> Submit Request</>
                                )}
                              </Button>
                              <Button
                                onClick={() => { setShowRequestForm(false); setRequestJustification(''); }}
                                variant="outline"
                                size="sm"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            onClick={() => setShowRequestForm(true)}
                            size="sm"
                            className="mt-2 bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <Send className="w-3.5 h-3.5 mr-1" /> Request Edit Access
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Month</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Total Employees</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Total Contractors</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b"
                          title="Optional — leave blank if the site uses no consultants. Feeds the EHS MIS 'No of Consultant' row.">
                        Total Consultants
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Employee Manhours</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Contractor Manhours</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b"
                          title="Optional. Counts toward Total Manhours and therefore toward TRIR / LTIFR / FR.">
                        Consultant Manhours
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b bg-blue-50"
                          title="Employee + Contractor + Consultant manhours. Used as the TRIR / LTIFR / FR denominator.">
                        Total Manhours
                      </th>
                      {hasHrmsData && (
                        <th className="px-4 py-3 text-center text-sm font-semibold text-blue-700 border-b bg-blue-50">HRMS</th>
                      )}
                      {canEdit && (
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {workforceData.map((row, idx) => {
                      const hrms = hrmsData[idx + 1];
                      const hasHrmsRow = !!hrms;
                      return (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2 border-b font-medium text-gray-700">
                            {MONTHS[idx]}
                          </td>
                          <td className="px-4 py-2 border-b">
                            <input
                              type="number"
                              min="0"
                              value={row.total_employees}
                              onChange={(e) => handleCellChange(idx, 'total_employees', e.target.value)}
                              readOnly={!canEdit}
                              className={`w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none ${!canEdit ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              placeholder={hasHrmsRow ? String(hrms.total_employees) : '0'}
                            />
                          </td>
                          <td className="px-4 py-2 border-b">
                            <input
                              type="number"
                              min="0"
                              value={row.total_contractors}
                              onChange={(e) => handleCellChange(idx, 'total_contractors', e.target.value)}
                              readOnly={!canEdit}
                              className={`w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none ${!canEdit ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              placeholder={hasHrmsRow ? String(hrms.total_contractors) : '0'}
                            />
                          </td>
                          <td className="px-4 py-2 border-b">
                            <input
                              type="number"
                              min="0"
                              value={row.total_consultants}
                              onChange={(e) => handleCellChange(idx, 'total_consultants', e.target.value)}
                              readOnly={!canEdit}
                              className={`w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none ${!canEdit ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              placeholder="—"
                            />
                          </td>
                          <td className="px-4 py-2 border-b">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={row.employee_manhours}
                              onChange={(e) => handleCellChange(idx, 'employee_manhours', e.target.value)}
                              readOnly={!canEdit}
                              className={`w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none ${!canEdit ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              placeholder="0"
                            />
                          </td>
                          <td className="px-4 py-2 border-b">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={row.contractor_manhours}
                              onChange={(e) => handleCellChange(idx, 'contractor_manhours', e.target.value)}
                              readOnly={!canEdit}
                              className={`w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none ${!canEdit ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              placeholder="0"
                            />
                          </td>
                          <td className="px-4 py-2 border-b">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={row.consultant_manhours}
                              onChange={(e) => handleCellChange(idx, 'consultant_manhours', e.target.value)}
                              readOnly={!canEdit}
                              className={`w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none ${!canEdit ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                              placeholder="—"
                            />
                          </td>
                          <td className="px-4 py-2 border-b bg-blue-50">
                            <input
                              type="number"
                              value={row.total_manhours}
                              readOnly
                              tabIndex={-1}
                              className="w-full px-2 py-1 border border-gray-200 rounded bg-gray-50 text-gray-700 font-semibold cursor-not-allowed"
                              placeholder="0"
                            />
                          </td>
                          {hasHrmsData && (
                            <td className="px-4 py-2 border-b text-center bg-blue-50">
                              {hasHrmsRow ? (
                                <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                          )}
                          {canEdit && (
                            <td className="px-4 py-2 border-b text-center">
                              <button
                                onClick={() => handleDeleteMonth(idx)}
                                className="text-red-500 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                                title={row.id ? `Delete ${MONTHS[idx]} data` : `Clear ${MONTHS[idx]} inputs`}
                                disabled={
                                  !row.id &&
                                  row.total_employees === '' &&
                                  row.total_contractors === '' &&
                                  row.total_consultants === '' &&
                                  row.employee_manhours === '' &&
                                  row.contractor_manhours === '' &&
                                  row.consultant_manhours === ''
                                }
                              >
                                <Trash2 className="w-4 h-4 mx-auto" />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {canEdit && (
                <div className="flex flex-wrap justify-end items-center mt-6 gap-3">
                  <span className="mr-auto text-xs text-gray-500">
                    Download the template to fill a whole year offline, then upload it.
                    Total Manhours is calculated as Employee + Contractor + Consultant
                    hours, and is the denominator for TRIR / LTIFR / FR.
                  </span>
                  <Button
                    variant="outline"
                    onClick={handleDownloadTemplate}
                    disabled={downloadingTemplate || !selectedSiteId || isAllSitesMode}
                  >
                    {downloadingTemplate ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileDown className="w-4 h-4 mr-2" />
                    )}
                    Download Template
                  </Button>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleImportFile}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => importInputRef.current?.click()}
                    disabled={importing}
                  >
                    {importing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Upload CSV
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {saving ? 'Saving...' : 'Save Workforce Data'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default WorkforceInput;
